/* eslint-disable camelcase */
import { supabaseClient } from './supabaseClient'
import { PROCESSED_STATUS } from './constants'
export { hasUncheckedWarehouseReviewItems, requiresWarehouseReview } from './warehouseReviewChecklist'

export type WarehouseReviewDecision = {
  decision_code: string
  decision_reason: string
  merchant_key: string
  active_references: number
  historical_despaired_orders: number
  rule_version: string
}

type WarehouseReviewItem = {
  deleted?: boolean | null
  disabled?: boolean | null
  quantity?: number | null
  url?: string | null
}

type WarehouseReviewOrder = {
  status_id?: number | null
  items?: WarehouseReviewItem[] | null
}

// Warehouse only needs a decision while the order is still operational. This
// deliberately keeps completed, returned, cancelled and pending orders off the
// reader path.
export const needsWarehouseReview = (order?: WarehouseReviewOrder | null): boolean => {
  return Number(order?.status_id) === PROCESSED_STATUS
}

// Mirror the database fast lane using the item data that is already present in
// the warehouse detail query. This path must not wait for any RPC or profile.
export const isOneReferenceOneUnit = (order?: WarehouseReviewOrder | null): boolean => {
  const activeItems = (order?.items || []).filter((item) => !item.deleted && !item.disabled)
  const activeUnits = activeItems.reduce((total, item) => total + Math.max(Number(item.quantity) || 1, 1), 0)
  return activeItems.length === 1 && activeUnits === 1
}

export const isAliExpressMerchant = (merchantKey?: string | null): boolean => {
  return /(^|\.)aliexpress\./i.test(merchantKey || '')
}

export const hasAliExpressWarehouseItem = (order?: WarehouseReviewOrder | null): boolean => {
  return (order?.items || [])
    .filter((item) => !item.deleted && !item.disabled)
    .some((item) => isAliExpressMerchant(item.url))
}

export const localWarehouseReviewDecision = (order?: WarehouseReviewOrder | null): WarehouseReviewDecision | null => {
  if (!needsWarehouseReview(order)) return null
  // AliExpress consolidates distinct orders in one shipment.  Warehouse must
  // always verify its contents, irrespective of units, references or profile.
  if (hasAliExpressWarehouseItem(order)) {
    return {
      decision_code: 'REVISAR_ALIEXPRESS',
      decision_reason: 'Pedido AliExpress; revisar siempre por consolidación de pedidos',
      merchant_key: 'aliexpress',
      active_references: (order?.items || []).filter((item) => !item.deleted && !item.disabled).length,
      historical_despaired_orders: 0,
      rule_version: 'warehouse-review-aliexpress-v1'
    }
  }
  if (!isOneReferenceOneUnit(order)) return null
  return {
    decision_code: 'NO_REVISAR_1_REFERENCIA',
    decision_reason: 'Una referencia y una unidad',
    merchant_key: '',
    active_references: 1,
    historical_despaired_orders: 0,
    rule_version: 'warehouse-review-local-fast-path-v1'
  }
}

export const getWarehouseReviewDecision = async (orderId: number): Promise<WarehouseReviewDecision> => {
  const { data, error } = await supabaseClient.rpc('warehouse_review_decision', { p_order_id: orderId })
  if (error) throw error

  const decision = Array.isArray(data) ? data[0] : data
  if (!decision) throw new Error('La decisión de revisión no devolvió datos')
  return decision as WarehouseReviewDecision
}
